import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Building2, Plus } from 'lucide-react';
import { FilterBar, SelectFilter, SearchInput, DateRangeFilter, ClearFilters } from '@/components/filters';
import { buildQuery, type DateRange } from '@/lib/filters';
import { StatTiles } from '@/components/StatTiles';
import { PageHeader } from '@/components/PageHeader';
import { Pagination, DEFAULT_PAGE_SIZE } from '@/components/Pagination';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Customer {
  id: string;
  name: string;
  email: string;
  company?: string;
  type: string;
  status: string;
  createdAt?: number;
  created_at?: number;
}

function Customers() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    company: '',
    type: 'INDIVIDUAL',
    status: 'ACTIVE',
  });

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>({});

  useEffect(() => {
    const id = setTimeout(fetchCustomers, search ? 300 : 0);
    return () => clearTimeout(id);
  }, [search, typeFilter, statusFilter, dateRange.from, dateRange.to, page]);

  // A filter change invalidates the current page — otherwise you can sit on
  // page 5 of a result set that now has one page and see an empty table.
  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter, dateRange.from, dateRange.to]);

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('all');
    setStatusFilter('all');
    setDateRange({});
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const qs = buildQuery({
        limit: DEFAULT_PAGE_SIZE,
        page,
        search,
        type: typeFilter,
        status: statusFilter,
        from: dateRange.from,
        to: dateRange.to,
      });
      const response = await fetch(`${apiUrl}/api/customers${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data.data || []);
        setTotal(data.total ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newCustomer),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewCustomer({ name: '', email: '', company: '', type: 'INDIVIDUAL', status: 'ACTIVE' });
        fetchCustomers();
      }
    } catch (error) {
      console.error('Failed to create customer:', error);
    }
  };

  const handleEditCustomer = async () => {
    if (!selectedCustomer) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/customers/${selectedCustomer.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(selectedCustomer),
      });

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedCustomer(null);
        fetchCustomers();
      }
    } catch (error) {
      console.error('Failed to update customer:', error);
    }
  };

  const openEditDialog = (customer: Customer) => {
    setSelectedCustomer({ ...customer });
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Catalog"
        icon={Building2}
        title={t('customers.title')}
        subtitle={t('customers.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('customers.addCustomer')}
          </Button>
        )}
      />

      <StatTiles stats={[
        { label: t('customers.stats.matching', { defaultValue: 'Matching results' }), value: total },
        { label: t('customers.stats.active', { defaultValue: 'Active on page' }), value: customers.filter((c) => c.status === 'ACTIVE').length, color: 'text-success' },
        { label: t('customers.stats.inactive', { defaultValue: 'Inactive on page' }), value: customers.filter((c) => c.status === 'INACTIVE').length },
        { label: t('customers.stats.business', { defaultValue: 'Business on page' }), value: customers.filter((c) => c.type === 'BUSINESS').length },
        { label: t('customers.stats.individual', { defaultValue: 'Individual on page' }), value: customers.filter((c) => c.type === 'INDIVIDUAL').length },
      ]} />

      <Card>
        <CardHeader>
          <CardTitle>{t('customers.allCustomers')}</CardTitle>
          <CardDescription>
            {t('customers.description', { count: total })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FilterBar>
            <SearchInput value={search} onChange={setSearch} placeholder="Name or email…" />
            <SelectFilter
              label={t('customers.table.type')}
              value={typeFilter}
              onChange={setTypeFilter}
              options={['INDIVIDUAL', 'BUSINESS'].map((v) => ({ value: v, label: v }))}
            />
            <SelectFilter
              label={t('customers.table.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              options={['ACTIVE', 'INACTIVE'].map((v) => ({ value: v, label: v }))}
            />
            <DateRangeFilter label="Created" value={dateRange} onChange={setDateRange} />
            <ClearFilters onClick={clearFilters} />
          </FilterBar>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('customers.table.name')}</TableHead>
                <TableHead>{t('customers.table.email')}</TableHead>
                <TableHead>{t('customers.table.company')}</TableHead>
                <TableHead>{t('customers.table.type')}</TableHead>
                <TableHead>{t('customers.table.status')}</TableHead>
                <TableHead>{t('licenses.createdAt')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-medium">{customer.name}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>{customer.company || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                        {customer.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={customer.status} />
                    </TableCell>
                    <TableCell>
                      {formatDate(customer.created_at || customer.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(customer)}>
                        {t('common.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination page={page} pageSize={DEFAULT_PAGE_SIZE} total={total} onPageChange={setPage} label="customers" />
        </CardContent>
      </Card>

      {/* Create Customer Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('customers.dialogs.create.title')}</DialogTitle>
            <DialogDescription>
              {t('customers.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('customers.table.name')}</Label>
              <Input
                id="name"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t('customers.table.email')}</Label>
              <Input
                id="email"
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="company">{t('customers.table.company')} ({t('common.optional')})</Label>
              <Input
                id="company"
                value={newCustomer.company}
                onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
                placeholder="Acme Inc."
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="type">{t('customers.table.type')}</Label>
              <Select
                value={newCustomer.type}
                onValueChange={(value) => setNewCustomer({ ...newCustomer, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                  <SelectItem value="BUSINESS">Business</SelectItem>
                  <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateCustomer} disabled={!newCustomer.name || !newCustomer.email}>
              {t('customers.createCustomer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('customers.dialogs.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('customers.dialogs.edit.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">{t('customers.table.name')}</Label>
                <Input
                  id="edit-name"
                  value={selectedCustomer.name}
                  onChange={(e) => setSelectedCustomer({ ...selectedCustomer, name: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-email">{t('customers.table.email')}</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={selectedCustomer.email}
                  onChange={(e) => setSelectedCustomer({ ...selectedCustomer, email: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-company">{t('customers.table.company')}</Label>
                <Input
                  id="edit-company"
                  value={selectedCustomer.company || ''}
                  onChange={(e) => setSelectedCustomer({ ...selectedCustomer, company: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-type">{t('customers.table.type')}</Label>
                <Select
                  value={selectedCustomer.type}
                  onValueChange={(value) => setSelectedCustomer({ ...selectedCustomer, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                    <SelectItem value="BUSINESS">Business</SelectItem>
                    <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-status">{t('customers.table.status')}</Label>
                <Select
                  value={selectedCustomer.status}
                  onValueChange={(value) => setSelectedCustomer({ ...selectedCustomer, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t('licenses.active')}</SelectItem>
                    <SelectItem value="INACTIVE">{t('licenses.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditCustomer}>
              {t('common.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Customers;
